/**
 * CancelSubscriptionModal - Multi-step modal for subscription cancellation.
 *
 * Step 1: Reason Selection - User selects why they're canceling
 * Step 2: Confirmation - Shows preview of what will happen (refund, dates)
 * Step 3: Result - Shows success/error and final details
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Image,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconReceipt } from "@tabler/icons-react";
import { previewCancellation, cancelSubscription } from "../api";
import {
  CANCELLATION_REASONS,
  type CancellationPreview,
  type CancellationReason,
  type CancelSubscriptionResponse,
} from "../types";

type Step = "reason" | "confirm" | "result";

interface CancelSubscriptionModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: (result: CancelSubscriptionResponse) => void;
}

export const CancelSubscriptionModal = ({
  opened,
  onClose,
  onSuccess,
}: CancelSubscriptionModalProps) => {
  const [step, setStep] = useState<Step>("reason");
  const [reason, setReason] = useState<CancellationReason | null>(null);
  const [additionalComments, setAdditionalComments] = useState("");
  const [preview, setPreview] = useState<CancellationPreview | null>(null);
  const [result, setResult] = useState<CancelSubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (opened) {
      setStep("reason");
      setReason(null);
      setAdditionalComments("");
      setPreview(null);
      setResult(null);
      setError(null);
    }
  }, [opened]);

  /**
   * Load cancellation preview when entering confirmation step.
   * Transitions to confirm step immediately to show loading state while fetching.
   */
  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setStep("confirm"); // Transition immediately to show loading state
    try {
      const data = await previewCancellation();
      setPreview(data);
    } catch (err: any) {
      console.error("Failed to load cancellation preview:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to load cancellation preview. Please try again."
      );
      setStep("reason"); // Return to reason step on error
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Execute the cancellation.
   */
  const handleConfirm = useCallback(async () => {
    if (!reason) return;

    setLoading(true);
    setError(null);
    try {
      const data = await cancelSubscription({
        reason,
        additional_comments: additionalComments || undefined,
      });
      setResult(data);
      setStep("result");
    } catch (err: any) {
      console.error("Failed to cancel subscription:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to cancel subscription. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [reason, additionalComments]);

  /**
   * Handle closing the result step.
   * Passes the cancellation result to onSuccess for handling (e.g., page refresh).
   */
  const handleResultClose = useCallback(() => {
    if (result) {
      onSuccess(result); // Pass result so parent can handle appropriately
    } else {
      onClose();
    }
  }, [result, onSuccess, onClose]);

  /**
   * Format a date string for display.
   */
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  /**
   * Check if feedback is required based on the selected reason.
   */
  const isFeedbackRequired =
    reason === "other" || reason === "missing_features";

  /**
   * Get dynamic placeholder text based on the selected reason.
   */
  const getFeedbackPlaceholder = () => {
    switch (reason) {
      case "missing_features":
        return "What features would you like to see?";
      case "other":
        return "Please tell us more about your reason for canceling...";
      default:
        return "We'd love to hear your feedback.";
    }
  };

  /**
   * Render Step 1: Reason Selection
   */
  const renderReasonStep = () => (
    <Stack gap="md">
      <Flex justify="center" align="center" gap="xl">
        <Stack w="100%">
          <Image
            src="https://d1ltrt1u8716a5.cloudfront.net/Wizzy/WizzyGoodbye.png"
            alt="Wizzy Goodbye"
            height={300}
            fit="contain"
          />
        </Stack>
        <Stack gap="lg" w="100%">
          <Text fz={28} fw={800} lh={1}>
            We're sorry to see you go.
          </Text>
          <Select
            label="Before you go, please tell us why you're canceling:"
            placeholder="Select a reason"
            data={CANCELLATION_REASONS}
            value={reason}
            onChange={(value) => setReason(value as CancellationReason)}
            required
          />

          <Textarea
            label="Tell us how we can do better:"
            placeholder={getFeedbackPlaceholder()}
            value={additionalComments}
            onChange={(e) => setAdditionalComments(e.currentTarget.value)}
            minRows={3}
            maxRows={5}
            required={isFeedbackRequired}
          />

          <Flex gap="sm" align="center">
            <Text fz={12} c="dimmed" fw={400}>
              You can reactivate your subscription anytime.
            </Text>
          </Flex>

          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {error}
            </Alert>
          )}

          <Group justify="space-between" mt="xl" gap="sm" w="100%" grow>
            <Button variant="outline" onClick={onClose} size="sm">
              Keep Subscription
            </Button>
            <Button
              onClick={loadPreview}
              size="sm"
              disabled={
                !reason || (isFeedbackRequired && !additionalComments.trim())
              }
              loading={loading}
            >
              Continue
            </Button>
          </Group>
        </Stack>
      </Flex>
    </Stack>
  );

  /**
   * Render Step 2: Confirmation with Preview
   */
  const renderConfirmStep = () => {
    if (!preview) return null;

    const remainingCredits = preview.totalCredits - preview.creditsUsed;

    return (
      <Stack gap="lg">
        <Text fz={24} fw={800}>
          Confirm Cancellation
        </Text>

        <Stack gap="md" px="xl">
          {/* What happens next */}
          <Flex justify="center" align="center" gap="xl">
            {preview.eligibleForRefund ? (
              <Stack gap="sm" w="100%">
                <Text fz={14}>
                  Your subscription will be cancelled{" "}
                  <Text span fw={500}>
                    immediately
                  </Text>{" "}
                  and you'll receive a prorated refund of:
                </Text>
                <Text fw={600} fz={24} w="100%" ta="center">
                  ${preview.refundAmount.toFixed(2)}
                </Text>

                <Text fz={13} c="dimmed">
                  Your refund is based on your return usage (
                  {preview.creditsUsed} of {preview.totalCredits} returns used).
                </Text>
              </Stack>
            ) : (
              <Stack gap="sm" w="100%">
                <Text fz={14}>
                  Your subscription will remain active until{" "}
                  <Text span fw={600}>
                    {formatDate(preview.effectiveDate)}
                  </Text>
                  . You can continue using SmartWiz until then.
                </Text>

                <Text fz={13} c="dimmed">
                  No refund will be issued as you've been subscribed for more
                  than 14 days.
                </Text>
              </Stack>
            )}

            {/* Postpaid pending charges warning */}
            {preview.isPostpaid && (
              <Alert color="orange" variant="light" p="md" radius="md" w="100%">
                <Stack gap="xs">
                  <Text fz={14} fw={600}>
                    Pending usage will be charged immediately
                  </Text>
                  <Text fz={13}>
                    Any unbilled usage will be finalized and charged to your
                    card on file when your subscription ends. This ensures all
                    returns processed are properly billed.
                  </Text>
                </Stack>
              </Alert>
            )}

            {/* Credit expiration warning - only show for prepaid plans */}
            {!preview.isPostpaid && remainingCredits > 0 && (
              <Alert color="blue" variant="light" p="md" radius="md" w="100%">
                <Stack gap="xs">
                  <Text fz={14} fw={600}>
                    Your {remainingCredits} remaining credits will expire
                  </Text>
                  <Text fz={13}>
                    Once your subscription ends, all unused return credits will
                    be permanently expired. Return credits cannot be
                    transferred, refunded, or used in the future if you decide
                    to resubscribe.
                  </Text>
                </Stack>
              </Alert>
            )}
          </Flex>
        </Stack>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}

        <Group justify="flex-end" mt="md" gap="sm">
          <Button
            variant="outline"
            onClick={() => setStep("reason")}
            size="sm"
            disabled={loading}
          >
            Go Back
          </Button>
          <Button
            color="red"
            onClick={handleConfirm}
            size="sm"
            loading={loading}
          >
            Confirm Cancellation
          </Button>
        </Group>
      </Stack>
    );
  };

  /**
   * Render Step 3: Result
   */
  const renderResultStep = () => {
    if (!result) return null;

    return (
      <Stack gap="md" align="center" py="lg">
        <ThemeIcon color="green" size={60} radius="xl">
          <IconCheck size={32} />
        </ThemeIcon>

        <Text fz={24} fw={800} ta="center">
          Subscription Cancelled
        </Text>

        <Card withBorder p="md" radius="md" w="100%">
          <Stack gap="sm">
            {result.cancelledImmediately ? (
              <>
                <Text fz={14} ta="center">
                  Your subscription has been cancelled immediately.
                </Text>
                {result.refundAmount && result.refundAmount > 0 && (
                  <Alert color="green" variant="light">
                    <Group gap="xs" justify="center">
                      <IconReceipt size={16} />
                      <Text fz={14} fw={600}>
                        A refund of ${result.refundAmount.toFixed(2)} has been
                        initiated
                      </Text>
                    </Group>
                  </Alert>
                )}
              </>
            ) : (
              <>
                <Text fz={14} ta="center">
                  Your subscription will remain active until:
                </Text>
                <Text fz={18} fw={600} ta="center" c="blue">
                  {formatDate(result.effectiveDate)}
                </Text>
                <Text fz={13} c="dimmed" ta="center">
                  You can continue using SmartWiz until this date.
                </Text>
              </>
            )}
          </Stack>
        </Card>

        <Button onClick={handleResultClose} size="md" fullWidth mt="md">
          Close
        </Button>
      </Stack>
    );
  };

  /**
   * Render loading state while fetching preview data.
   */
  const renderLoadingState = () => (
    <Flex justify="center" align="center" py="xl" mih={300}>
      <Stack align="center" gap="md">
        <Loader size="lg" />
        <Text c="dimmed" fz="sm">
          Loading your subscription details...
        </Text>
      </Stack>
    </Flex>
  );

  /**
   * Render the current step content.
   */
  const renderContent = () => {
    // Show loading state when fetching preview
    if (loading && step === "confirm" && !preview) {
      return renderLoadingState();
    }

    switch (step) {
      case "reason":
        return renderReasonStep();
      case "confirm":
        return renderConfirmStep();
      case "result":
        return renderResultStep();
      default:
        return null;
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={step === "result" ? handleResultClose : onClose}
      centered
      size="900px"
      radius="lg"
      padding="lg"
      withCloseButton={false}
    >
      <Box p={20}>{renderContent()}</Box>
    </Modal>
  );
};
